<script setup lang="ts">
import type { IBranchCard } from '~/components/entity/branch'
import { useEntityBranch } from '~/composables/useEntityBranch'
import { MapPin } from 'lucide-vue-next'

const props = defineProps<{
  item: IBranchCard
  selected?: boolean
}>()

const {
  getTypeLabels,
  getBranchTitle,
  formatAddress,
  getServicesWithIcons,
  getOrganizationTypeLabel
} = useEntityBranch()

const branchTitle = computed(() => getBranchTitle(props.item))
const services = computed(() => getServicesWithIcons(props.item.services))
</script>

<template>
  <article
    class="rounded-lg border p-4 transition-colors hover:border-blue-300"
    :class="props.item.highlight || props.selected ? 'border-blue-300' : 'border-gray-200'"
  >
    <div class="flex flex-col space-y-3">
      <div>
        <div class="mb-3">
          <Heading
            tag="h3"
            class="hit-title h6"
            v-html="props.item.highlightedTitle || branchTitle"
          />
        </div>
        <div class="space-y-2 text-sm text-gray-600">
          <div v-if="props.item.address_formatted" class="flex items-start gap-2">
            <MapPin class="mt-0.5 h-4 w-4 min-w-4 text-gray-400" />
            <span class="hit-title" v-html="formatAddress(props.item.address_formatted)" />
          </div>
        </div>
        <div class="mt-2 flex flex-col gap-4">
          <div class="flex-1 space-y-3">
            <div class="flex flex-wrap gap-1">
              <Badge v-if="props.item.organization_type" variant="blue" rounded="full">
                {{ getOrganizationTypeLabel(props.item.organization_type) }}
              </Badge>
              <Badge
                v-for="typeLabel in getTypeLabels(props.item.types)"
                :key="typeLabel"
                variant="blue"
                rounded="full"
              >
                {{ typeLabel }}
              </Badge>
            </div>

            <div v-if="services.length">
              <div class="flex flex-wrap gap-2">
                <Badge
                  v-for="service in services"
                  :key="service.key"
                  variant="blue"
                  rounded="full"
                  class="flex items-center gap-1.5 bg-blue-50 font-normal text-gray-700"
                >
                  <component :is="service.icon" class="h-3 w-3" />
                  {{ service.label }}
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </article>
</template>
